/**
 * src/components/ReportLogo.tsx
 * Pizza-slice pie-chart logo.
 *
 * disc = #808080, slice = #00FF00 @ 70%, cut-lines and centre = #00FF00 @ 70%
 * sliceIndex is 1-based; position = (sliceIndex-1)/total → sliceIndex/total
 */

interface ReportLogoProps {
  variant?:     "mensuel" | "trimestriel" | "annuel" | "app";
  sliceIndex?:  number;   // 1-based
  totalSlices?: number;   // overrides variant default
  size?:        number;
  className?:   string;
  style?:       React.CSSProperties;
}

const DEFAULT_TOTAL: Record<string, number> = {
  mensuel: 12, trimestriel: 4, annuel: 1, app: 4,
};

function polarToXY(cx: number, cy: number, r: number, deg: number) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

export default function ReportLogo({
  variant    = "app",
  sliceIndex = 1,
  totalSlices,
  size       = 32,
  className,
  style,
}: ReportLogoProps) {
  const cx     = size / 2;
  const cy     = size / 2;
  const discR  = size * 0.42;
  const sliceR = size * 0.50;
  const OPACITY = 0.7;
  const GREEN   = "#00FF00";
  const DISC    = "#b2b2b2";

  const total        = totalSlices ?? DEFAULT_TOTAL[variant] ?? 4;
  const degPer       = 360 / total;
  const isFullCircle = total === 1;
  const startDeg     = (sliceIndex - 1) * degPer;
  const endDeg       = sliceIndex * degPer;

  const slicePath = () => {
    if (isFullCircle) {
      return `M ${cx} ${cy - sliceR} A ${sliceR} ${sliceR} 0 1 1 ${cx - 0.001} ${cy - sliceR} Z`;
    }
    const s = polarToXY(cx, cy, sliceR, startDeg);
    const e = polarToXY(cx, cy, sliceR, endDeg);
    return [
      `M ${cx.toFixed(3)} ${cy.toFixed(3)}`,
      `L ${s.x.toFixed(3)} ${s.y.toFixed(3)}`,
      `A ${sliceR} ${sliceR} 0 ${degPer > 180 ? 1 : 0} 1 ${e.x.toFixed(3)} ${e.y.toFixed(3)}`,
      "Z",
    ].join(" ");
  };

  const lw      = size * 0.05;
  const startPt = polarToXY(cx, cy, sliceR, startDeg);
  const endPt   = polarToXY(cx, cy, sliceR, endDeg);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg" className={className} style={style} aria-hidden="true">
      <circle cx={cx} cy={cy} r={discR} fill={DISC}/>
      <path d={slicePath()} fill={GREEN} fillOpacity={OPACITY}/>
      {!isFullCircle && (<>
        <line x1={cx} y1={cy} x2={startPt.x} y2={startPt.y}
          stroke={GREEN} strokeOpacity={OPACITY} strokeWidth={lw} strokeLinecap="round"/>
        <line x1={cx} y1={cy} x2={endPt.x} y2={endPt.y}
          stroke={GREEN} strokeOpacity={OPACITY} strokeWidth={lw} strokeLinecap="round"/>
      </>)}
      <circle cx={cx} cy={cy} r={size * 0.06} fill={GREEN} fillOpacity={OPACITY}/>
    </svg>
  );
}
